/**
 * Unified Offline Storage Service
 * Consolidated service for all offline operations including:
 * - IndexedDB storage for incidents, hazards, audits
 * - Sync queue management with retry logic
 * - Cache management for offline read access
 * - Auto-sync on connection restoration
 * 
 * This merges offlineStorage.js and offlineQueue.js into a single service
 */

const DB_NAME = 'AeroGuardOfflineDB';
const DB_VERSION = 3; // Incremented for unified schema

// Store names
const STORES = {
  INCIDENTS: 'offline_incidents',
  HAZARDS: 'offline_hazards',
  AUDITS: 'offline_audits',
  SYNC_QUEUE: 'sync_queue',
  SUBMISSION_QUEUE: 'submissionQueue',
  CACHED_DATA: 'cached_data',
  USER_DATA: 'user_data'
};

// Queue item statuses
const QueueStatus = {
  PENDING: 'pending',
  SYNCING: 'syncing',
  SYNCED: 'synced',
  FAILED: 'failed'
};

// Form types that can be queued
const FormType = {
  HAZARD_REPORT: 'hazard_report',
  INCIDENT: 'incident',
  RISK_ASSESSMENT: 'risk_assessment',
  AUDIT_FINDING: 'audit_finding',
  CAPA: 'capa',
  SURVEY_RESPONSE: 'survey_response'
};

class UnifiedOfflineService {
  constructor() {
    this.db = null;
    this.isOnline = navigator.onLine;
    this.syncInProgress = false;
    this.listeners = new Set();
    this.initialized = false;
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnlineChange(true));
    window.addEventListener('offline', () => this.handleOnlineChange(false));
  }

  // ============== INITIALIZATION ==============

  /**
   * Initialize the IndexedDB database
   */
  async init() {
    if (this.initialized && this.db) {
      return this.db;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[UnifiedOffline] Failed to open database:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.initialized = true;
        console.log('[UnifiedOffline] Database opened successfully');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        console.log('[UnifiedOffline] Upgrading database schema...');

        // Create offline incidents store
        if (!db.objectStoreNames.contains(STORES.INCIDENTS)) {
          const incidentStore = db.createObjectStore(STORES.INCIDENTS, { keyPath: 'localId', autoIncrement: true });
          incidentStore.createIndex('syncStatus', 'syncStatus', { unique: false });
          incidentStore.createIndex('createdAt', 'createdAt', { unique: false });
          incidentStore.createIndex('serverId', 'serverId', { unique: false });
        }

        // Create offline hazards store
        if (!db.objectStoreNames.contains(STORES.HAZARDS)) {
          const hazardStore = db.createObjectStore(STORES.HAZARDS, { keyPath: 'localId', autoIncrement: true });
          hazardStore.createIndex('syncStatus', 'syncStatus', { unique: false });
          hazardStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Create offline audits store
        if (!db.objectStoreNames.contains(STORES.AUDITS)) {
          const auditStore = db.createObjectStore(STORES.AUDITS, { keyPath: 'localId', autoIncrement: true });
          auditStore.createIndex('syncStatus', 'syncStatus', { unique: false });
          auditStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Create unified sync queue store
        if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
          const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
          syncStore.createIndex('type', 'type', { unique: false });
          syncStore.createIndex('status', 'status', { unique: false });
          syncStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Create submission queue store (for backward compatibility)
        if (!db.objectStoreNames.contains(STORES.SUBMISSION_QUEUE)) {
          const subStore = db.createObjectStore(STORES.SUBMISSION_QUEUE, { keyPath: 'id' });
          subStore.createIndex('status', 'status', { unique: false });
          subStore.createIndex('formType', 'formType', { unique: false });
          subStore.createIndex('createdAt', 'createdAt', { unique: false });
        }

        // Create cached data store
        if (!db.objectStoreNames.contains(STORES.CACHED_DATA)) {
          const cacheStore = db.createObjectStore(STORES.CACHED_DATA, { keyPath: 'key' });
          cacheStore.createIndex('type', 'type', { unique: false });
          cacheStore.createIndex('updatedAt', 'updatedAt', { unique: false });
        }

        // Create user data store
        if (!db.objectStoreNames.contains(STORES.USER_DATA)) {
          db.createObjectStore(STORES.USER_DATA, { keyPath: 'key' });
        }

        console.log('[UnifiedOffline] Database schema upgrade complete');
      };
    });
  }

  // ============== ONLINE/OFFLINE HANDLING ==============

  handleOnlineChange(isOnline) {
    this.isOnline = isOnline;
    console.log(`[UnifiedOffline] Network status: ${isOnline ? 'ONLINE' : 'OFFLINE'}`);
    
    // Notify listeners
    this.listeners.forEach(listener => listener({ 
      type: 'networkChange',
      isOnline 
    }));
    
    // Trigger sync when coming online
    if (isOnline && !this.syncInProgress) {
      this.syncAll();
    }
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getOnlineStatus() {
    return this.isOnline;
  }

  // ============== SAFE TRANSACTION HELPERS ==============

  hasStore(storeName) {
    if (!this.db) return false;
    return this.db.objectStoreNames.contains(storeName);
  }

  safeTransaction(storeName, mode = 'readonly') {
    if (!this.db) {
      console.warn('[UnifiedOffline] Database not initialized');
      return null;
    }
    if (!this.hasStore(storeName)) {
      console.warn(`[UnifiedOffline] Store "${storeName}" not found`);
      return null;
    }
    try {
      return this.db.transaction([storeName], mode);
    } catch (error) {
      console.error(`[UnifiedOffline] Transaction failed for ${storeName}:`, error);
      return null;
    }
  }

  // ============== GENERIC CRUD OPERATIONS ==============

  async addItem(storeName, item) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.safeTransaction(storeName, 'readwrite');
      if (!transaction) {
        reject(new Error(`Store "${storeName}" not available`));
        return;
      }
      const store = transaction.objectStore(storeName);
      
      const itemWithMeta = {
        ...item,
        syncStatus: item.syncStatus || 'pending',
        createdAt: item.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const request = store.add(itemWithMeta);
      
      request.onsuccess = () => {
        resolve({ ...itemWithMeta, localId: request.result });
      };
      
      request.onerror = () => {
        reject(request.error);
      };
    });
  }

  async getAllItems(storeName, indexName = null, indexValue = null) {
    await this.init();
    return new Promise((resolve) => {
      const transaction = this.safeTransaction(storeName, 'readonly');
      if (!transaction) {
        resolve([]);
        return;
      }
      const store = transaction.objectStore(storeName);
      
      let request;
      if (indexName && indexValue !== null) {
        const index = store.index(indexName);
        request = index.getAll(indexValue);
      } else {
        request = store.getAll();
      }
      
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => resolve([]);
    });
  }

  async getItem(storeName, key) {
    await this.init();
    return new Promise((resolve) => {
      const transaction = this.safeTransaction(storeName, 'readonly');
      if (!transaction) {
        resolve(null);
        return;
      }
      const store = transaction.objectStore(storeName);
      const request = store.get(key);
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    });
  }

  async updateItem(storeName, item) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.safeTransaction(storeName, 'readwrite');
      if (!transaction) {
        reject(new Error(`Store "${storeName}" not available`));
        return;
      }
      const store = transaction.objectStore(storeName);
      
      const updatedItem = {
        ...item,
        updatedAt: new Date().toISOString()
      };
      
      const request = store.put(updatedItem);
      request.onsuccess = () => resolve(updatedItem);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteItem(storeName, key) {
    await this.init();
    return new Promise((resolve) => {
      const transaction = this.safeTransaction(storeName, 'readwrite');
      if (!transaction) {
        resolve(false);
        return;
      }
      const store = transaction.objectStore(storeName);
      const request = store.delete(key);
      
      request.onsuccess = () => resolve(true);
      request.onerror = () => resolve(false);
    });
  }

  // ============== INCIDENT OPERATIONS ==============

  async saveIncidentOffline(incidentData) {
    const item = await this.addItem(STORES.INCIDENTS, {
      ...incidentData,
      offlineCreated: true
    });
    await this.addToSyncQueue('incident', 'create', item);
    return item;
  }

  async getPendingIncidents() {
    return this.getAllItems(STORES.INCIDENTS, 'syncStatus', 'pending');
  }

  async markIncidentSynced(localId, serverId) {
    const incident = await this.getItem(STORES.INCIDENTS, localId);
    if (incident) {
      incident.syncStatus = 'synced';
      incident.serverId = serverId;
      await this.updateItem(STORES.INCIDENTS, incident);
    }
  }

  // ============== HAZARD OPERATIONS ==============

  async saveHazardOffline(hazardData) {
    const item = await this.addItem(STORES.HAZARDS, {
      ...hazardData,
      offlineCreated: true
    });
    await this.addToSyncQueue('hazard', 'create', item);
    return item;
  }

  async getPendingHazards() {
    return this.getAllItems(STORES.HAZARDS, 'syncStatus', 'pending');
  }

  // ============== WILDLIFE STRIKE OPERATIONS ==============
  // Reuses the HAZARDS store for local persistence (wildlife strikes are
  // a specialised hazard type) and tags the sync-queue entry with
  // type='wildlife_strike' so processSyncItem routes it to the correct
  // endpoint (/api/wildlife-strikes/).
  async saveWildlifeStrikeOffline(strikeData) {
    const item = await this.addItem(STORES.HAZARDS, {
      ...strikeData,
      _kind: 'wildlife_strike',
      offlineCreated: true,
    });
    await this.addToSyncQueue('wildlife_strike', 'create', item);
    return item;
  }

  // ============== UNIFIED SYNC QUEUE ==============

  async addToSyncQueue(type, action, data, metadata = {}) {
    await this.init();
    
    const queueItem = {
      id: `queue_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      action,
      data,
      metadata: {
        ...metadata,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      },
      status: QueueStatus.PENDING,
      retryCount: 0,
      maxRetries: 3,
      createdAt: new Date().toISOString(),
      lastAttempt: null,
      error: null
    };

    return new Promise((resolve, reject) => {
      const transaction = this.safeTransaction(STORES.SYNC_QUEUE, 'readwrite');
      if (!transaction) {
        reject(new Error('Sync queue not available'));
        return;
      }
      const store = transaction.objectStore(STORES.SYNC_QUEUE);
      const request = store.add(queueItem);
      
      request.onsuccess = () => {
        window.dispatchEvent(new CustomEvent('offlineQueueUpdated', { 
          detail: { action: 'added', item: queueItem } 
        }));
        // Register a Background Sync so the queue drains even if the tab
        // is closed before connectivity returns. Browsers without sync
        // support (e.g. Safari) silently no-op — we fall back to the
        // online-event + visibility-change listeners further down.
        if ('serviceWorker' in navigator && 'SyncManager' in window) {
          navigator.serviceWorker.ready
            .then((reg) => reg.sync.register('aeroguard-drain'))
            .catch((err) => console.debug('[Offline] sync.register failed:', err));
        }
        resolve({ ...queueItem, id: request.result });
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingSyncItems() {
    return this.getAllItems(STORES.SYNC_QUEUE, 'status', QueueStatus.PENDING);
  }

  async updateSyncItemStatus(id, status, error = null) {
    const item = await this.getItem(STORES.SYNC_QUEUE, id);
    if (item) {
      item.status = status;
      item.error = error;
      item.lastAttempt = new Date().toISOString();
      if (status === QueueStatus.FAILED) {
        item.retryCount = (item.retryCount || 0) + 1;
      }
      await this.updateItem(STORES.SYNC_QUEUE, item);
    }
  }

  async getQueueStats() {
    const allItems = await this.getAllItems(STORES.SYNC_QUEUE);
    return {
      total: allItems.length,
      pending: allItems.filter(i => i.status === QueueStatus.PENDING).length,
      syncing: allItems.filter(i => i.status === QueueStatus.SYNCING).length,
      synced: allItems.filter(i => i.status === QueueStatus.SYNCED).length,
      failed: allItems.filter(i => i.status === QueueStatus.FAILED).length,
      byType: allItems.reduce((acc, item) => {
        acc[item.type] = (acc[item.type] || 0) + 1;
        return acc;
      }, {})
    };
  }

  // ============== SYNC OPERATIONS ==============

  async syncAll() {
    if (this.syncInProgress || !this.isOnline) {
      return { synced: 0, failed: 0, message: 'Sync skipped' };
    }

    this.syncInProgress = true;
    console.log('[UnifiedOffline] Starting sync...');

    const results = { synced: 0, failed: 0, errors: [] };

    try {
      const pendingItems = await this.getPendingSyncItems();
      console.log(`[UnifiedOffline] Found ${pendingItems.length} items to sync`);

      window.dispatchEvent(new CustomEvent('offlineSyncStarted', { 
        detail: { count: pendingItems.length } 
      }));

      for (const item of pendingItems) {
        try {
          await this.syncItem(item);
          await this.updateSyncItemStatus(item.id, QueueStatus.SYNCED);
          results.synced++;
        } catch (error) {
          console.error(`[UnifiedOffline] Failed to sync item ${item.id}:`, error);
          await this.updateSyncItemStatus(item.id, QueueStatus.FAILED, error.message);
          results.failed++;
          results.errors.push({ id: item.id, error: error.message });
        }
        
        // Small delay between syncs
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      console.log(`[UnifiedOffline] Sync complete: ${results.synced} synced, ${results.failed} failed`);
    } finally {
      this.syncInProgress = false;
    }

    // Notify listeners
    this.listeners.forEach(listener => listener({ 
      type: 'syncComplete', 
      results 
    }));

    window.dispatchEvent(new CustomEvent('offlineSyncCompleted', { 
      detail: results 
    }));

    return results;
  }

  async syncItem(queueItem) {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const token = await this.getUserData('authToken');

    if (!token) {
      throw new Error('No auth token available');
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };

    let endpoint, method;
    
    switch (queueItem.type) {
      case 'incident':
        endpoint = `${API_URL}/api/incidents`;
        method = queueItem.action === 'create' ? 'POST' : 'PUT';
        break;
      case 'hazard':
      case 'hazard_report':
        endpoint = `${API_URL}/api/hazards`;
        method = queueItem.action === 'create' ? 'POST' : 'PUT';
        break;
      case 'audit':
      case 'audit_finding':
        endpoint = `${API_URL}/api/audits`;
        method = queueItem.action === 'create' ? 'POST' : 'PUT';
        break;
      case 'risk_assessment':
        endpoint = `${API_URL}/api/risks`;
        method = queueItem.action === 'create' ? 'POST' : 'PUT';
        break;
      case 'capa':
        endpoint = `${API_URL}/api/capas`;
        method = queueItem.action === 'create' ? 'POST' : 'PUT';
        break;
      case 'wildlife_strike':
        // Trailing slash is required — server expects POST /api/wildlife-strikes/
        endpoint = `${API_URL}/api/wildlife-strikes/`;
        method = 'POST';
        break;
      default:
        throw new Error(`Unknown sync type: ${queueItem.type}`);
    }

    // Remove offline-specific fields
    const { localId, syncStatus, offlineCreated, _kind, ...syncData } = queueItem.data;

    const response = await fetch(endpoint, {
      method,
      headers,
      body: JSON.stringify(syncData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server error: ${response.status} - ${errorText}`);
    }

    const result = await response.json();

    // Update local record with server ID
    if (queueItem.type === 'incident' && queueItem.data.localId) {
      await this.markIncidentSynced(queueItem.data.localId, result.id);
    }

    return result;
  }

  // ============== FORM SUBMISSION WITH OFFLINE SUPPORT ==============

  async submitWithOfflineSupport(formType, endpoint, method, data, metadata = {}) {
    if (this.isOnline) {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = await this.getUserData('authToken') || localStorage.getItem('token');
        
        const response = await fetch(`${API_URL}${endpoint}`, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(data)
        });
        
        if (response.ok) {
          const result = await response.json();
          return { 
            success: true, 
            online: true, 
            data: result,
            message: 'Submitted successfully'
          };
        }
        throw new Error(`Server error: ${response.status}`);
      } catch (error) {
        // If online submission fails due to network, queue it
        if (error.message.includes('fetch') || !navigator.onLine) {
          const queueItem = await this.addToSyncQueue(formType, 'create', data, metadata);
          return { 
            success: true, 
            online: false, 
            queued: true, 
            queueId: queueItem.id,
            message: 'Saved offline. Will sync when back online.'
          };
        }
        throw error;
      }
    } else {
      // Device is offline, queue the submission
      const queueItem = await this.addToSyncQueue(formType, 'create', data, metadata);
      return { 
        success: true, 
        online: false, 
        queued: true, 
        queueId: queueItem.id,
        message: 'You\'re offline. Saved locally and will sync automatically when back online.'
      };
    }
  }

  // ============== CACHE OPERATIONS ==============

  async cacheData(key, type, data) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.safeTransaction(STORES.CACHED_DATA, 'readwrite');
      if (!transaction) {
        reject(new Error('Cache store not available'));
        return;
      }
      const store = transaction.objectStore(STORES.CACHED_DATA);
      
      const cacheItem = {
        key,
        type,
        data,
        updatedAt: new Date().toISOString()
      };
      
      const request = store.put(cacheItem);
      request.onsuccess = () => resolve(cacheItem);
      request.onerror = () => reject(request.error);
    });
  }

  async getCachedData(key) {
    const item = await this.getItem(STORES.CACHED_DATA, key);
    return item?.data || null;
  }

  async clearOldCache(maxAgeHours = 24) {
    const allCached = await this.getAllItems(STORES.CACHED_DATA);
    const cutoff = new Date(Date.now() - maxAgeHours * 60 * 60 * 1000).toISOString();
    
    for (const item of allCached) {
      if (item.updatedAt < cutoff) {
        await this.deleteItem(STORES.CACHED_DATA, item.key);
      }
    }
  }

  // ============== USER DATA ==============

  async saveUserData(key, value) {
    await this.init();
    return new Promise((resolve, reject) => {
      const transaction = this.safeTransaction(STORES.USER_DATA, 'readwrite');
      if (!transaction) {
        reject(new Error('User data store not available'));
        return;
      }
      const store = transaction.objectStore(STORES.USER_DATA);
      const request = store.put({ key, value });
      
      request.onsuccess = () => resolve(value);
      request.onerror = () => reject(request.error);
    });
  }

  async getUserData(key) {
    const item = await this.getItem(STORES.USER_DATA, key);
    return item?.value || null;
  }

  // ============== UTILITY METHODS ==============

  async clearAllData() {
    await this.init();
    const storeNames = Object.values(STORES);
    
    for (const storeName of storeNames) {
      if (this.hasStore(storeName)) {
        await new Promise((resolve) => {
          const transaction = this.db.transaction([storeName], 'readwrite');
          const store = transaction.objectStore(storeName);
          const request = store.clear();
          request.onsuccess = () => resolve();
          request.onerror = () => resolve();
        });
      }
    }
    
    console.log('[UnifiedOffline] All data cleared');
  }

  async getStats() {
    await this.init();
    const stats = {};
    
    for (const [name, storeName] of Object.entries(STORES)) {
      const items = await this.getAllItems(storeName);
      stats[name] = items.length;
    }
    
    return stats;
  }

  async clearSyncedItems() {
    const items = await this.getAllItems(STORES.SYNC_QUEUE);
    let count = 0;
    
    for (const item of items) {
      if (item.status === QueueStatus.SYNCED) {
        await this.deleteItem(STORES.SYNC_QUEUE, item.id);
        count++;
      }
    }
    
    return count;
  }

  async retryFailedItems() {
    const items = await this.getAllItems(STORES.SYNC_QUEUE);
    let count = 0;
    
    for (const item of items) {
      if (item.status === QueueStatus.FAILED) {
        item.status = QueueStatus.PENDING;
        item.retryCount = 0;
        item.error = null;
        await this.updateItem(STORES.SYNC_QUEUE, item);
        count++;
      }
    }
    
    window.dispatchEvent(new CustomEvent('offlineQueueUpdated', { 
      detail: { action: 'retryAll', count } 
    }));
    
    return count;
  }

  /**
   * Setup automatic sync when coming back online
   */
  setupAutoSync() {
    // Already handled in constructor
    // Also sync on page visibility change
    document.addEventListener('visibilitychange', async () => {
      if (document.visibilityState === 'visible' && this.isOnline) {
        const stats = await this.getQueueStats();
        if (stats.pending > 0) {
          console.log('[UnifiedOffline] Tab visible with pending items, initiating sync...');
          await this.syncAll();
        }
      }
    });
  }
}

// Create singleton instance
const unifiedOfflineService = new UnifiedOfflineService();

// Export for backward compatibility
export default unifiedOfflineService;
export { STORES, QueueStatus, FormType };

// Named exports for specific functions (backward compatibility with offlineQueue.js)
export const initOfflineDB = () => unifiedOfflineService.init();
export const addToQueue = (formType, endpoint, method, data, metadata) => 
  unifiedOfflineService.addToSyncQueue(formType, 'create', { ...data, endpoint, method }, metadata);
export const getPendingQueue = () => unifiedOfflineService.getPendingSyncItems();
export const getFullQueue = () => unifiedOfflineService.getAllItems(STORES.SYNC_QUEUE);
export const removeFromQueue = (id) => unifiedOfflineService.deleteItem(STORES.SYNC_QUEUE, id);
export const syncQueue = () => unifiedOfflineService.syncAll();
export const getQueueStats = () => unifiedOfflineService.getQueueStats();
export const clearSyncedItems = () => unifiedOfflineService.clearSyncedItems();
export const retryFailedItems = () => unifiedOfflineService.retryFailedItems();
export const setupAutoSync = () => unifiedOfflineService.setupAutoSync();
export const submitWithOfflineSupport = (api, formType, endpoint, method, data, metadata) =>
  unifiedOfflineService.submitWithOfflineSupport(formType, endpoint, method, data, metadata);
export const isOnline = () => unifiedOfflineService.getOnlineStatus();
